import { VodBackgroundContext } from "../newVodDetail/vodModalNavigation";
import React, { useCallback, useContext, useEffect, useRef } from "react";
import videojs from "video.js";
import "videojs-contrib-eme";
import { playerSource, previewRange } from "../../api/mw/playback";

const playerOptions = {
  fill: true,
  fluid: true,
  responsive: true,
  preload: "auto",
  controls: false,
  liveui: false,
  // Start explicitly after source, range and mute state have been configured.
  autoplay: false,
  loop: true
};

const TrailerPlayer = ({
  playUrl,
  poster,
  isAudioOn,
  isPlay = true,
  isFrom = "",
  onAutoplayMuted
}) => {
  const covered = useContext(VodBackgroundContext);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const playAttemptRef = useRef(0);
  const rangeCleanupRef = useRef(null);
  const presentationRef = useRef(null);
  presentationRef.current = { poster, isAudioOn, onAutoplayMuted };
  const hasPlayUrl = typeof playUrl === "string" && playUrl.trim().length > 0;

  const startPlayback = useCallback(() => {
    const player = playerRef.current;
    if (covered || !isPlay || !hasPlayUrl || !player || player.isDisposed()) return;
    const attempt = ++playAttemptRef.current;
    const isCurrent = () =>
      attempt === playAttemptRef.current && !player.isDisposed();
    const showPoster = () => {
      if (isCurrent()) player.posterImage.show();
    };
    const result = player.play();
    if (result && result.catch) result.catch((error) => {
      if (!isCurrent()) return;
      // Browsers may reject audible autoplay even when the viewer's sound
      // preference is on. Retry muted and keep the visible audio control in sync.
      if (error?.name === "NotAllowedError" && !player.muted()) {
        player.muted(true);
        player.volume(0);
        const onMuted = presentationRef.current.onAutoplayMuted;
        if (onMuted) onMuted();
        const retry = player.play();
        if (retry && retry.catch) retry.catch(showPoster);
      } else {
        showPoster();
      }
    });
  }, [covered, isPlay, hasPlayUrl]);

  useEffect(() => {
    const { poster, isAudioOn } = presentationRef.current;
    playAttemptRef.current++;
    if (rangeCleanupRef.current) rangeCleanupRef.current();
    rangeCleanupRef.current = null;
    if (!hasPlayUrl) {
      if (playerRef.current) {
        playerRef.current.pause();
        playerRef.current.reset();
        playerRef.current.poster(poster);
        playerRef.current.posterImage.show();
      }
      return;
    }
    if (!playerRef.current) {
      const player = videojs(videoRef.current, playerOptions);
      playerRef.current = player;
      player.on("pause", () => player.posterImage.show());
      player.on("play", () => player.posterImage.hide());
    }
    const player = playerRef.current;
    player.muted(!isAudioOn);
    player.volume(isAudioOn ? 1 : 0);
    player.poster(poster);
    const source = playerSource(playUrl);
    const range = previewRange(playUrl);
    if (source.keySystems && player.eme) player.eme();
    if (range) {
      const seekStart = () => player.currentTime(range.start);
      const enforceRange = () => {
        if (player.currentTime() >= range.end || player.currentTime() < range.start)
          seekStart();
      };
      player.one("loadedmetadata", seekStart);
      player.on("timeupdate", enforceRange);
      rangeCleanupRef.current = () => {
        player.off("loadedmetadata", seekStart);
        player.off("timeupdate", enforceRange);
      };
    }
    player.src([source]);
  }, [playUrl, hasPlayUrl]);

  useEffect(() => () => {
    playAttemptRef.current++;
    if (rangeCleanupRef.current) rangeCleanupRef.current();
    if (playerRef.current) playerRef.current.dispose();
    playerRef.current = null;
  }, []);

  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.muted(!isAudioOn);
      playerRef.current.volume(isAudioOn ? 1 : 0);
      playerRef.current.poster(poster);
    }
  }, [isAudioOn, poster]);

  useEffect(() => {
    if (playerRef.current) {
      if (covered || !isPlay || !hasPlayUrl) {
        playAttemptRef.current++;
        playerRef.current.pause();
        playerRef.current.posterImage.show();
      } else {
        startPlayback();
      }
    }
  }, [playUrl, isPlay, covered, hasPlayUrl, startPlayback]);

  return (
    <div
      className="trailer-video-wrapper"
      onClick={(e) => {
        if (hasPlayUrl && playerRef.current && e.target.localName === "video") {
          if (playerRef.current.paused()) {
            startPlayback();
          } else {
            playAttemptRef.current++;
            playerRef.current.pause();
          }
        }
      }}
    >
      <video
        ref={videoRef}
        id={`trailer-player-${isFrom}`}
        className="video-js trailer-videojs-player"
        poster={poster}
        playsInline
      />
    </div>
  );
};

export default TrailerPlayer;
